import { createAction, createReducer } from 'deox';
import { pick } from 'ramda';

// IMPORTANT!
// Must update the instantCreditKeys array if this interface is updated.
//
// There's no way good way to convert the interface keys into an array at the
// moment, and there's no way to enforce that the array contains all elements
// of a union, so TS cannot check that the array is exhaustive.
export interface InstantCredit {
  returnCode?: string;
  errorCode?: string;
  firstName?: string;
  middleInitial?: string;
  lastName?: string;
  accountNumber?: string;
  callId?: string;
  virtualCardAccountNumber?: string;
  expirationDate?: string;
  expirationType?: string;
  expirationValue?: number;
  securityCode?: string;
  firstPurchaseLimit?: number;
  authAllowed?: number;
  apr?: string;
  rejectReasons?: string;
  creditLimit?: number;
  bureauCode?: string;
  bureauScore?: string;
  openToBuy?: string;
  cardType?: string;
  notBestAprContent?: string;
  message?: string;
  hasExistingAccount?: boolean;
  isApproved?: boolean;
  isDeclined?: boolean;
  rewards?: Rewards;
  applicationId?: string;
  accountId?: string;
}

export interface Rewards {
  currentLevelName: string;
  currentLevelNumber: string | number;
  certificates: Certificate[];
}

export interface Certificate {
  certificateNumber: string;
  fulfillDate: string;
  expireDate: string; // 'yyyy-mm-dd'
  rewardAmount: number;
  barcodeIndicator?: '128' | '3of9';
  pinCode?: string;
}

const instantCreditKeys: (keyof InstantCredit)[] = [
  'returnCode',
  'errorCode',
  'firstName',
  'middleInitial',
  'lastName',
  'accountNumber',
  'callId',
  'virtualCardAccountNumber',
  'expirationDate',
  'expirationType',
  'expirationValue',
  'securityCode',
  'firstPurchaseLimit',
  'authAllowed',
  'apr',
  'rejectReasons',
  'creditLimit',
  'bureauCode',
  'bureauScore',
  'openToBuy',
  'cardType',
  'notBestAprContent',
  'message',
  'hasExistingAccount',
  'isApproved',
  'isDeclined',
  'rewards',
  'applicationId',
  'accountId',
];

export interface InstantCreditState {
  entity: InstantCredit;
  meta: {
    timedOut: boolean;
  };
}

export const defaultState: InstantCreditState = {
  entity: {},
  meta: {
    /**
     * Instant credit is considered timed out by default (inaccessible)
     * until request hooks sets data on it (accessible)
     * When it timed out, we reset the IC, turning it back to be inaccessible.
     */
    timedOut: true,
  },
};

export const mergeInstantCredit = createAction(
  'MERGE_INSTANT_CREDIT',
  resolve => (data: Partial<InstantCredit>) => resolve(data),
);

export const resetInstantCredit = createAction('RESET_INSTANT_CREDIT');

export const instantCreditReducer = createReducer(
  defaultState,
  handleAction => [
    handleAction(mergeInstantCredit, (state, { payload: data }) => {
      return {
        ...state,
        entity: {
          ...state.entity,
          ...pick(instantCreditKeys, data),
        },
        meta: {
          timedOut: false,
        },
      };
    }),

    handleAction(resetInstantCredit, () => defaultState),
  ],
);

export interface MountedState {
  instantCredit: ReturnType<typeof instantCreditReducer>;
}

export const selectInstantCredit = (state: MountedState): InstantCredit =>
  state.instantCredit.entity;

export const selectInstantCreditTimedOut = (state: MountedState): boolean =>
  state.instantCredit.meta.timedOut;

export default instantCreditReducer;
